import React, { useEffect, useState } from "react";
import { Eye, EyeOff, Save, Zap, Loader2, RefreshCw } from "lucide-react";
import { Panel, MonoLabel, LiveDot } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

const KEY_LABEL = { razorpay: "Key Secret", whatsapp: "Access Token", twilio: "Auth Token", msg91: "Auth Key" };
const ID_LABEL = { razorpay: "Key ID", whatsapp: "Phone Number ID", twilio: "Account SID" };

export function Integrations({ eventId }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/events/${eventId}/integrations`).then((r) => setRows(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { setRows(null); load(); }, [eventId]); // eslint-disable-line

  if (!rows) return <div className="mono-label" data-testid="integrations-loading">Loading integrations…</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="integrations-grid">
      {rows.map((r) => <IntegrationCard key={r.id} row={r} onChange={(u) => setRows(rows.map((x) => x.id === u.id ? u : x))} />)}
    </div>
  );
}

function IntegrationCard({ row, onChange }) {
  const [editing, setEditing] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [keyId, setKeyId] = useState(row.provider === "whatsapp" ? (row.extra?.phone_number_id || "") : (row.key_id || ""));
  const [hook, setHook] = useState(row.extra?.webhook_secret || "");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(row.last_test_result);
  const hasHook = row.provider === "razorpay";

  const save = async () => {
    const body = {};
    if (newKey) body.api_key = newKey;
    if (row.provider === "whatsapp") body.extra = { ...(row.extra || {}), phone_number_id: keyId };
    else if (keyId !== (row.key_id || "")) body.key_id = keyId;
    if (hasHook && hook !== (row.extra?.webhook_secret || "")) body.extra = { ...(row.extra || {}), webhook_secret: hook.trim() };
    if (newKey && row.provider === "razorpay") body.key_prefix = keyId.startsWith("rzp_live_") ? "rzp_live_" : "rzp_test_";
    if (!Object.keys(body).length) return toast.info("Nothing to save");
    setBusy("save");
    try {
      const { data } = await api.patch(`/integrations/${row.id}`, body);
      onChange(data); setNewKey(""); setEditing(false);
      toast.success(`${row.name} saved · key encrypted at rest`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`/integrations/${row.id}/test`);
      setResult(data); onChange({ ...row, status: data.status, last_test_result: data });
      (data.ok ? toast.success : toast.error)(`${row.name}: ${data.message}`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  const statusColor = row.status === "connected" ? "green" : "red";
  return (
    <Panel className="p-6" data-testid={`int-card-${row.provider}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif text-xl">{row.name}</div>
          <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>Provider · {row.provider.toUpperCase()}</div>
        </div>
        <span data-testid={`int-status-${row.provider}`}><LiveDot color={statusColor} label={row.status.replace("_", " ").toUpperCase()} /></span>
      </div>

      {ID_LABEL[row.provider] && (
        <div className="mt-5">
          <MonoLabel>{ID_LABEL[row.provider]}</MonoLabel>
          <input data-testid={`int-keyid-${row.provider}`} className="input-x mt-2 !font-mono" value={keyId} onChange={(e) => setKeyId(e.target.value)} placeholder="public identifier" />
        </div>
      )}

      <div className="mt-4">
        <MonoLabel>{KEY_LABEL[row.provider] || "API Key"}</MonoLabel>
        <div className="mt-2 flex items-stretch">
          {editing ? (
            <input data-testid={`int-key-${row.provider}`} type={show ? "text" : "password"} className="input-x flex-1 !font-mono" value={newKey}
              onChange={(e) => setNewKey(e.target.value)} placeholder="paste new secret" autoFocus />
          ) : (
            <input data-testid={`int-key-${row.provider}`} className="input-x flex-1 !font-mono !tracking-widest" value={row.api_key_masked || "— not set —"} readOnly
              onClick={() => setEditing(true)} />
          )}
          <button data-testid={`int-toggle-${row.provider}`} onClick={() => editing ? setShow(!show) : setEditing(true)} className="btn-ghost !px-3 !py-3 border-l-0">
            {editing && show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
          {editing ? "Secrets are encrypted before storage and never returned in full." : "Click the field to replace the secret."}
        </div>
      </div>

      {hasHook && (
        <div className="mt-4">
          <MonoLabel>Webhook Secret</MonoLabel>
          <div className="mt-2 flex items-stretch">
            <input data-testid="int-webhook-secret-razorpay" className="input-x flex-1 !font-mono" value={hook}
              onChange={(e) => setHook(e.target.value)} placeholder="paste the same string you set in Razorpay → Webhooks" />
            <button data-testid="int-webhook-generate-razorpay" type="button" className="btn-ghost !px-3 !py-3 border-l-0"
              title="Generate a strong secret"
              onClick={() => {
                const s = Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, "0")).join("");
                setHook(s); navigator.clipboard?.writeText(s);
                toast.success("Secret generated and copied — paste it into Razorpay → Webhooks, then Save here");
              }}>
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
            Must match Razorpay Dashboard → Settings → Webhooks (events: payment.captured, payment.failed, order.paid).
            Keys starting <span className="font-mono">rzp_live_</span> take real money; <span className="font-mono">rzp_test_</span> is a sandbox.
          </div>
        </div>
      )}

      {result && (
        <div data-testid={`int-result-${row.provider}`} className="mt-4 p-3 hairline text-sm" style={{ color: result.ok ? "var(--green)" : "var(--red-2)" }}>
          <span className="font-mono text-[11px] tracking-widest uppercase">{result.ok ? "OK" : "FAIL"}{result.http ? ` · HTTP ${result.http}` : ""}</span>
          <div className="mt-1" style={{ color: "var(--text)" }}>{result.message}</div>
          <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{new Date(result.tested_at).toLocaleString()}</div>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button data-testid={`int-test-${row.provider}`} onClick={test} disabled={!!busy} className="btn-ghost">
          {busy === "test" ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Test connection
        </button>
        <button data-testid={`int-save-${row.provider}`} onClick={save} disabled={!!busy} className="btn-red">
          {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>
    </Panel>
  );
}
